/**
 * Global project status colour coding.
 * Use when no API colour is provided, or for timeline/custom views that need hex colours.
 * See: frontend-docs/docs/standards/project-status-colours.md
 */

/** Normalise status name for lookup: lowercase, spaces to underscores */
function normaliseStatusName(name) {
  if (name == null) return '';
  if (typeof name === 'object') {
    const n = name?.global_status?.name ?? name?.name ?? name;
    return normaliseStatusName(n);
  }
  return String(name).toLowerCase().trim().replace(/\s+/g, '_');
}

/** PrimeVue severity for Badge (success | info | warn | danger | secondary) */
const STATUS_SEVERITY_MAP = {
  draft: 'warn',
  quote: 'info',
  confirmed: 'success',
  in_progress: 'info',
  completed: 'secondary',
  complete: 'secondary',
  cancelled: 'danger',
  lost: 'danger',
  dead: 'danger',
  not_happening: 'danger'
};

/** Hex colours for timeline/warehouse/custom blocks (bg and border) */
const SEVERITY_HEX = {
  success: { bg: '#dcfce7', border: '#22c55e' },
  info: { bg: '#dbeafe', border: '#3b82f6' },
  warn: { bg: '#fef3c7', border: '#f59e0b' },
  danger: { bg: '#fee2e2', border: '#ef4444' },
  secondary: { bg: '#f3f4f6', border: '#9ca3af' }
};

/**
 * Get PrimeVue Badge severity for a project status name.
 * @param {string} statusName - e.g. from project.status?.global_status?.name || project.status?.name || project.status
 * @returns {string} 'success' | 'info' | 'warn' | 'danger' | 'secondary'
 */
export function getProjectStatusSeverity(statusName) {
  const key = normaliseStatusName(statusName);
  return STATUS_SEVERITY_MAP[key] ?? 'secondary';
}

/**
 * Get background and border hex colours for timeline/warehouse blocks.
 * @param {string} statusName - same as getProjectStatusSeverity
 * @returns {{ bg: string, border: string, severity: string }}
 */
export function getProjectStatusColors(statusName) {
  const severity = getProjectStatusSeverity(statusName);
  const { bg, border } = SEVERITY_HEX[severity] ?? SEVERITY_HEX.secondary;
  return { bg, border, severity };
}

/**
 * Get Tailwind-style class string for status (e.g. bg-*-100 text-*-800).
 * Useful for elements that use class instead of inline style.
 * @param {string} statusName
 * @returns {string} class list
 */
export function getProjectStatusTailwindClasses(statusName) {
  const severity = getProjectStatusSeverity(statusName);
  const map = {
    success: 'bg-green-100 text-green-800 hover:bg-green-200',
    info: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
    warn: 'bg-amber-100 text-amber-800 hover:bg-amber-200',
    danger: 'bg-red-100 text-red-800 hover:bg-red-200',
    secondary: 'bg-gray-100 text-gray-800 hover:bg-gray-200'
  };
  return map[severity] ?? map.secondary;
}
